/*
 * CONSTANTS
 */
export const PAGE_SIZE = 20
const _CACHE_TIME_MS = 60000
const _MAX_CACHE_PAGES = 20


/*
 * OBJECTS
 */
export const createPagination = (fetchPage, publish) => {
  let _generation = 0
  let _offset = 0
  let _search = ''
  let _isDisposed = false
  let _requestController = null
  const _cache = new Map()
  const _itemIds = new Set()
  let _state = {
    'items': [],
    'loading': false,
    'hasMore': true,
    'error': null
  }

  // Publish state only while the selector is mounted.
  const _Publish = patch => {
    _state = { ..._state, ...patch }
    if (!_isDisposed) publish(_state)
  }

  // Cancel a stale HTTP request before starting another search.
  const _CancelRequest = () => {
    _requestController?.abort()
    _requestController = null
  }

  // Keep a small time-limited cache without allowing memory growth.
  const _CachePage = (key, rows) => {
    if (_cache.size >= _MAX_CACHE_PAGES) _cache.delete(_cache.keys().next().value)
    _cache.set(key, { rows, 'createdAt': Date.now() })
  }

  // Fetch the next server page and ignore stale search responses.
  const _LoadMore = async () => {
    if (_isDisposed || _state.loading || !_state.hasMore) return void 0

    const _RequestGeneration = _generation
    const _Variables = {
      'take': PAGE_SIZE,
      'skip': _offset,
      'search': _search || void 0
    }
    const _CacheKey = JSON.stringify(_Variables)
    const _CachedPage = _cache.get(_CacheKey)
    const _HasValidCache = _CachedPage && Date.now() - _CachedPage.createdAt < _CACHE_TIME_MS
    const _RequestController = _HasValidCache ? null : new AbortController()

    _Publish({ 'loading': true, 'error': null })

    try {
      if (_HasValidCache) {
        _cache.delete(_CacheKey)
        _cache.set(_CacheKey, _CachedPage)
      } else {
        if (_CachedPage) _cache.delete(_CacheKey)
        _requestController = _RequestController
      }

      const _Rows = _HasValidCache
        ? _CachedPage.rows
        : await fetchPage(_Variables, _RequestController.signal)

      if (_requestController === _RequestController) _requestController = null

      if (_isDisposed || _RequestGeneration !== _generation) return void 0
      if (!_HasValidCache) _CachePage(_CacheKey, _Rows)

      const _NewRows = _Rows.filter(item => {
        if (!item.id || !item.displayName || _itemIds.has(item.id)) return false
        _itemIds.add(item.id)
        return true
      })
      _offset += _Rows.length
      _Publish({
        'items': _state.items.concat(_NewRows),
        'loading': false,
        'hasMore': _Rows.length === PAGE_SIZE
      })
    } catch (_) {
      if (_requestController === _RequestController) _requestController = null
      if (!_isDisposed && _RequestGeneration === _generation) {
        _Publish({
          'loading': false,
          'error': 'Unable to load options. Please retry.'
        })
      }
    }
    return void 0
  }

  // Return pagination controls.
  return {
    'loadMore': _LoadMore,
    'invalidate': () => {
      _generation++
      _offset = 0
      _itemIds.clear()
      _CancelRequest()

      // Avoid publishing the same cleared state for every keystroke.
      if (_state.items.length || !_state.loading || _state.hasMore || _state.error) {
        _Publish({
          'items': [],
          'loading': true,
          'hasMore': false,
          'error': null
        })
      }
    },
    'reset': value => {
      _generation++
      _offset = 0
      _search = value.trim()
      _itemIds.clear()
      _CancelRequest()
      _Publish({
        'items': [],
        'loading': false,
        'hasMore': true,
        'error': null
      })
      return _LoadMore()
    },
    'dispose': () => {
      _generation++
      _isDisposed = true
      _CancelRequest()
      _cache.clear()
    }
  }
}
